import { createStyles, Divider, Grid, Paper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';

import { IPermissionsTemplate, IRole } from '~/src/dash/api/permissions/model';
import { updateRole } from '~/src/dash/api/permissions/service';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { PermissionTree } from '~/src/dash/components/permission-tree';
import { IStore } from '~/src/dash/reducers/store';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';

interface IState {
  selectedRole?: IRole;
  filterRole: string;
  openCreateRoleDialog: boolean;
  newRoleName: string;
  selectedRolePermissions: string;
}

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IProps {
  permissionsTemplate: IPermissionsTemplate;
  availableRoles: IRole[];
  refreshAvailableRoles: () => void;
  displayError: (str: string) => void;
  displaySuccess: (str: string) => void;
  openCreateRoleDialog: () => void;
}

export const ModelRolesManager = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '600px',
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    constructor(props: IProps) {
      super(props);

      let currentRole = ((props.availableRoles as IRole[]) || [undefined]).find(
        (value: IRole) => value.id === props.user.user.PermissionRole.id,
      );
      if (currentRole === undefined) {
        currentRole = ((props.availableRoles as IRole[]) || [undefined])[0];
      }

      this.state = {
        selectedRole: currentRole,
        filterRole: '',
        openCreateRoleDialog: false,
        newRoleName: '',
        selectedRolePermissions: '',
      };
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
      // Selected Role
      if (prevProps.availableRoles !== this.props.availableRoles) {
        let selectedRole = undefined as IRole | undefined;
        if (
          this.props.availableRoles !== undefined &&
          this.props.availableRoles.length > 0 &&
          this.state.selectedRole !== undefined
        ) {
          selectedRole = this.props.availableRoles[this.props.availableRoles.length - 1];
        }

        if (selectedRole === undefined) {
          selectedRole = ((this.props.availableRoles as IRole[]) || [undefined])[0];
        }

        this.setState({
          selectedRole,
        });
      }
    }

    savePermissions = () => {
      if (this.state.selectedRole === undefined) {
        return;
      }
      // todo put new permissions

      const role = this.state.selectedRole;
      role.permissions = this.state.selectedRolePermissions;

      updateRole(role, parseInt(this.props.session.publisher.ID, 10)).then(([updatedRole, err]) => {
        if (err !== undefined) {
          this.props.displayError(err.toString());
        } else {
          if (updatedRole !== undefined) {
            this.props.displaySuccess('Role ' + updatedRole.name + ' successfully updated!');
          }
          this.props.refreshAvailableRoles();
        }
      });
    };

    isSavePermissionDisabled = (): boolean => {
      const { user } = this.props;
      const { selectedRole } = this.state;

      // Only nabhan can save builtin roles (pub id = 0)
      if (selectedRole !== null && selectedRole !== undefined) {
        if (selectedRole.publisherID === 0 && user.user.ID !== '5') {
          return true;
        }
      }

      if (hasRoleSuperUser(user.user)) {
        return false;
      }

      if (selectedRole === null || selectedRole === undefined) {
        return true;
      }

      if (!IS_AUTHORIZED(['roles_admin'], user.user).isAuthorized) {
        return true;
      }

      return false;
    };

    onChangeSearchRole = (_: any, value: IRole) => {
      if (value === null) {
        return;
      }

      const selectedRole = this.props.availableRoles.find((r: IRole) => {
        return r.id === value.id;
      });

      this.setState({
        selectedRole,
        selectedRolePermissions: (selectedRole || { permissions: '' }).permissions,
      });
    };

    render() {
      return (
        <Paper
          style={{ maxWidth: '800px' }}
          classes={{
            root: this.props.classes.paper,
          }}
        >
          <Grid>
            {this.state.selectedRole === undefined ? (
              <div>Loading...</div>
            ) : (
              <Grid container direction='row' justify='flex-start' alignItems='center' spacing={1}>
                <Grid item xs={6}>
                  <Autocomplete
                    placeholder='Search Role'
                    id='role-list-search-input'
                    openOnFocus
                    options={this.props.availableRoles}
                    value={this.state.selectedRole}
                    getOptionLabel={(option: IRole) => {
                      if (option.publisherID === 0) {
                        return option.name + ' (built-in)';
                      }
                      return option.name;
                    }}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField {...params} label='Search Role' variant='outlined' />
                    )}
                    onChange={this.onChangeSearchRole}
                  />
                </Grid>
                <Grid item>
                  <ModelSecondaryButton
                    onClick={this.props.openCreateRoleDialog}
                    disabled={!IS_AUTHORIZED(['roles_admin'], this.props.user.user).isAuthorized}
                  >
                    + New Custom Role
                  </ModelSecondaryButton>
                </Grid>
              </Grid>
            )}
            <Grid>
              <Divider style={{ height: '2px', marginTop: '16px', marginBottom: '6px' }} />
              {this.props.permissionsTemplate === undefined ||
              this.state.selectedRole === undefined ? (
                <div>Loading...</div>
              ) : (
                <PermissionTree
                  disabled={this.isSavePermissionDisabled()}
                  permissionsTemplate={this.props.permissionsTemplate}
                  selectedPermissions={(this.state.selectedRole || { permissions: '' }).permissions}
                  savePermissions={this.savePermissions}
                  onCheckedChanges={(checked: string[]) => {
                    this.setState({
                      selectedRolePermissions: checked.join(', '),
                    });
                  }}
                  publisherID={this.props.session.publisher.ID}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      );
    }
  },
);
